import {
  ActionIcon,
  Badge,
  Box,
  createStyles,
  Grid,
  Group,
  Pagination,
  Paper,
  Stack,
  Text,
  useMantineTheme
} from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { IconExternalLink } from '@tabler/icons'
import Link from 'next/link'
import React from 'react'

import { DateField } from '@/components/commons/DisplayData'
import { Ticket } from '@/components/commons/DisplayStatus'
import { PictureCard } from '@/components/surfaces'
import { Ticket as TicketType } from '@/types'

interface Props {
  count: number
  page: number
  pageSize: number
  tickets: TicketType[]
  handleQuery: (page: number) => void
}

const useStyles = createStyles(theme => ({
  paper: { '&:hover': { backgroundColor: theme.colors.gray[0] } },
  routerLink: {
    display: 'block',
    padding: theme.spacing.md,
    textDecoration: 'none'
  }
}))

export default function TicketsList({ count, page, pageSize, tickets, handleQuery }: Props) {
  // Hooks
  const { classes } = useStyles()
  const theme = useMantineTheme()
  const isSm = useMediaQuery(`(min-width: ${theme.breakpoints.sm}px)`)

  // Constants
  const primaryColor = theme.colors[theme.primaryColor][7]

  return (
    <>
      {tickets.map(ticket => {
        const {
          category,
          dateCreated,
          dateUpdated,
          id,
          institution,
          order,
          seller,
          status: ticketStatus,
          statusDisplay: ticketStatusDisplay,
          title
        } = ticket || {}
        return (
          <Paper key={id} mb="sm" className={classes.paper} withBorder>
            <Box component={Link} href={`/help/tickets/${id}`} className={classes.routerLink}>
              <Grid gutter={2} align="center">
                <Grid.Col xs={12} sm="auto">
                  <Box>
                    <Text size="xl" weight="bold" color={theme.colors[theme.primaryColor][7]}>
                      # {id}{' '}
                      <Badge color="blue" hidden={isSm}>
                        {category?.name}
                      </Badge>
                    </Text>
                  </Box>
                </Grid.Col>
                <Grid.Col xs={12} sm={6}>
                  <Stack spacing={5}>
                    <Box sx={{ maxWidth: '250px' }}>
                      <Text weight="bold">
                        {title}{' '}
                        <Badge color="blue" hidden={!isSm}>
                          {category?.name}
                        </Badge>
                      </Text>
                    </Box>
                    {order ? (
                      <Group spacing={2} align="center">
                        <Text>
                          Pedido <strong>{order?.orderNumber}</strong>
                        </Text>
                        <ActionIcon
                          variant="subtle"
                          size="md"
                          color={primaryColor}
                          onClick={(e: React.MouseEvent) => {
                            e.preventDefault()
                            e.stopPropagation()
                            window?.open(
                              `/accounts/orders/${order?.orderNumber}`,
                              '_blank',
                              'noopener,noreferrer'
                            )
                          }}>
                          <IconExternalLink size={18} />
                        </ActionIcon>
                      </Group>
                    ) : null}
                    {institution ? (
                      <PictureCard title={institution?.displayName} picture={institution?.logo} />
                    ) : null}
                    <Text size="xs">
                      <em>
                        Criado em <DateField type="datetime" value={dateCreated} />
                      </em>
                    </Text>
                    <Text size="xs">
                      <em>
                        Última atualização em <DateField type="datetime" value={dateUpdated} />
                      </em>
                    </Text>
                  </Stack>
                </Grid.Col>
                <Grid.Col xs={12} sm={3}>
                  <PictureCard
                    title={seller?.displayName}
                    subtitle="Vendedor"
                    subtitlePosition="before"
                    picture={seller?.logo}
                  />
                </Grid.Col>
                <Grid.Col xs={12} sm="auto">
                  <Ticket status={ticketStatus} statusDisplay={ticketStatusDisplay} />
                </Grid.Col>
              </Grid>
            </Box>
          </Paper>
        )
      })}

      {Math.ceil(count / pageSize) > 1 && (
        <Group position="right" p="md" hidden={Math.ceil(count / pageSize) < 2}>
          <Pagination
            total={Math.ceil(count / pageSize)}
            siblings={2}
            page={page}
            onChange={(currentPage: number) => handleQuery(currentPage)}
          />
        </Group>
      )}
    </>
  )
}
